import { Navigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useWallet } from '~/wallet';
import { AppPage } from '~/ui/AppPage';
import { Button, Card, Chip, EmptyState } from '~/ui/primitives';
import { capabilityLabel, formatDuration } from '~/ui/format';

function hostLabel(origin: string): string {
  try {
    return new URL(origin).host || origin;
  } catch {
    return origin;
  }
}

export const Delegations = observer(() => {
  const { identity, delegationsStore } = useWallet();

  if (!identity.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const issued = delegationsStore.listIssued();
  const approvals = delegationsStore.listApprovals();

  return (
    <AppPage title="Delegations">
      {/*
        CEF-AI/wallet#92. Browsers key a framed document's storage on the site
        that embeds it, so the wallet running inside an app has a different
        store from this one, and neither can read the other. This page can
        therefore only ever show what was granted HERE.

        Saying so is not a nicety. Without it the page reads as a complete
        account-wide list, and an embedded user — the common case — sees an
        empty page and concludes they have granted nothing. That is a wrong
        answer delivered confidently, which is worse than no page at all.
      */}
      <Card>
        <p style={{ margin: 0, font: '400 14px/1.6 var(--font-sans)', color: 'var(--text-secondary)' }}>
          <strong style={{ color: 'var(--text-primary)' }}>This page lists what you granted here.</strong> Your browser
          keeps the wallet&rsquo;s storage separate for each app that embeds it, so access you approved from inside an
          app is recorded and managed in that app, not on this page. Nothing is missing from your account — the record
          lives with the app it belongs to.
        </p>
      </Card>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <h2 style={{ margin: 0, font: '600 16px/1.2 var(--font-sans)' }}>Issued</h2>
        <p style={{ margin: 0, font: '400 13px/1.5 var(--font-sans)', color: 'var(--text-secondary)' }}>
          Records of access the wallet has granted. An issued grant cannot be called back — it stops working when it
          expires. <strong>Forget</strong> removes the record from this device, not the access.
        </p>
        {issued.length === 0 ? (
          <Card>
            <EmptyState
              title="Nothing granted from this page"
              hint="Access you approved inside an app is recorded there, not here."
            />
          </Card>
        ) : (
          issued.map((d) => (
            <Card key={d.token}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 'var(--space-4)',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {d.capabilities.map((c) => (
                      <Chip key={c} tone="violet">
                        {capabilityLabel(c)}
                      </Chip>
                    ))}
                  </div>
                  <span style={{ font: '400 13px/1.5 var(--font-sans)', color: 'var(--text-secondary)' }}>
                    Granted to <Chip tone="mono">{hostLabel(d.hostOrigin)}</Chip> · expires{' '}
                    {new Date(d.expiresAtMs).toLocaleString()}
                  </span>
                </div>
                <Button
                  variant="tertiary"
                  size="sm"
                  fullWidth={false}
                  onClick={() => delegationsStore.forgetIssued(d.token)}
                >
                  Forget
                </Button>
              </div>
            </Card>
          ))
        )}
      </section>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <h2 style={{ margin: 0, font: '600 16px/1.2 var(--font-sans)' }}>Auto-approve rules</h2>
        <p style={{ margin: 0, font: '400 13px/1.5 var(--font-sans)', color: 'var(--text-secondary)' }}>
          <strong>Revoke</strong> stops an app being approved automatically next time. It does not cancel access already
          granted; anything issued keeps working until it expires.
        </p>
        {approvals.length === 0 ? (
          <Card>
            <EmptyState
              title="No auto-approve rules on this page"
              hint={'Choosing "Always approve" inside an app creates a rule you manage in that app.'}
            />
          </Card>
        ) : (
          approvals.map((a) => (
            <Card key={`${a.hostOrigin}-${a.capabilitiesHash}`}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 'var(--space-4)',
                }}
              >
                <span style={{ font: '400 14px/1.5 var(--font-sans)' }}>
                  <Chip tone="mono">{hostLabel(a.hostOrigin)}</Chip> · up to {formatDuration(a.ttlCapSeconds)}
                </span>
                <Button
                  variant="tertiary"
                  size="sm"
                  fullWidth={false}
                  onClick={() => delegationsStore.revokeApproval(a)}
                >
                  Revoke
                </Button>
              </div>
            </Card>
          ))
        )}
      </section>
    </AppPage>
  );
});
